import { PayloadAction, createAction, createSlice } from "@reduxjs/toolkit";
import { cloneDeep } from "lodash";
import { OrganisationContact } from "../../organisation/objects/organisationContact";
import { Preferences } from "../../profile/objects/preferences";
import { EMPTY_MINI_NETWORK_LIST_CONFIGURATION, EMPTY_MINI_NETWORK_MARGIN_LIST_CONFIGURATION, EMPTY_MINI_NETWORK_MARGIN_LIST_CRITERIA, EMPTY_NETWORK, EMPTY_NETWORK_LIST_CRITERIA, MINI_NETWORK_MARGIN_MODULE, MINI_NETWORK_MODULE } from "../constant/miniNetworkConstant";
import { MiniNetworkState } from "../objects/miniNetworkstate";
import { Network } from "../objects/network";
import { NetworkListCriteria } from "../objects/networkListCriteria";
import { setMiniNetworkPreference } from "../utils/setMiniNetworkPreference";
import { Margin } from "../../margin/objects/margin";
import { MarginListCriteria } from "../objects/marginListCriteria";
import { setMarginPreference } from "../utils/setMarginPreference";
import { Column } from "../objects/column";


const initialState: MiniNetworkState = {
    mini_network_list: [],
    mini_network_list_configuration: EMPTY_MINI_NETWORK_LIST_CONFIGURATION,
    mini_network_list_loading: false,
    mini_network_list_count: 0,
    mini_network_list_criteria: EMPTY_NETWORK_LIST_CRITERIA,
    mini_network: EMPTY_NETWORK,
    mini_network_contacts: [],
    mini_network_contacts_loading: false,
    mini_network_contact: null,
    mini_network_sub_networks: [],
    mini_network_sub_networks_loading: false,
    mini_network_sub_network: null,
    margins: [],
    margins_configuration: EMPTY_MINI_NETWORK_MARGIN_LIST_CONFIGURATION,
    margin: null,
    margins_loading: false,
    margins_count: 0,
    margins_criteria: EMPTY_MINI_NETWORK_MARGIN_LIST_CRITERIA,
    suspended_margin: null,
    first_preference_loaded: false

};

export const setPreferences = createAction<Preferences[]>('PROFILE_SET_PREFERENCES');


const setSuspendedMarginReducer = (state: MiniNetworkState, action: PayloadAction<Margin | null>): MiniNetworkState => ({
    ...state, suspended_margin: action.payload
});

const addMarginsConfigurationPreferenceReducer = (state: MiniNetworkState, action: PayloadAction<Preferences[]>): MiniNetworkState => ({
    ...state, margins_configuration: { ...state.margins_configuration, preferences: [...state.margins_configuration.preferences, ...action.payload] }
});

const setMarginsConfigurationPreferenceReducer = (state: MiniNetworkState, action: PayloadAction<Preferences[]>): MiniNetworkState => ({
    ...state, margins_configuration: { ...state.margins_configuration, preferences: action.payload }
});


const moveDownColumnMarginsReducer = (state: MiniNetworkState, action: PayloadAction<number>): MiniNetworkState => {
    const columns = cloneDeep([...state.margins_configuration.columns]);
    for (let i = 0; i < columns.length; i++) {
        columns[i].last_updated = false;
    }
    const column = { ...columns[action.payload + 1] };
    columns[action.payload + 1] = columns[action.payload];
    columns[action.payload + 1].last_updated = true;
    columns[action.payload] = column;
    return { ...state, margins_configuration: { ...state.margins_configuration, columns } };
};


const moveUpColumnMarginsReducer = (state: MiniNetworkState, action: PayloadAction<number>): MiniNetworkState => {
    const columns = cloneDeep([...state.margins_configuration.columns]);
    for (let i = 0; i < columns.length; i++) {
        columns[i].last_updated = false;
    }
    const column = { ...columns[action.payload - 1] };
    columns[action.payload - 1] = columns[action.payload];
    columns[action.payload - 1].last_updated = true;
    columns[action.payload] = column;
    return { ...state, margins_configuration: { ...state.margins_configuration, columns } };
};

const toggleColumnMarginsReducer = (state: MiniNetworkState, action: PayloadAction<number>): MiniNetworkState => {
    const columns = cloneDeep([...state.margins_configuration.columns]);
    const column = { ...columns[action.payload] };
    column.checked = !column.checked;
    columns[action.payload] = column;
    return { ...state, margins_configuration: { ...state.margins_configuration, columns } };
};

const setNetworkMarginsReducer = (state: MiniNetworkState, action: PayloadAction<Margin[]>): MiniNetworkState => ({
    ...state, margins: action.payload
});
const updateMarginsCriteriaReducer = (state: MiniNetworkState, action: PayloadAction<MarginListCriteria>): MiniNetworkState => ({
    ...state, margins_criteria: action.payload
});
const updateMarginReducer = (state: MiniNetworkState, action: PayloadAction<Margin | null>): MiniNetworkState => ({
    ...state, margin: action.payload
});
const updateMarginsLoadingReducer = (state: MiniNetworkState, action: PayloadAction<boolean>): MiniNetworkState => ({
    ...state, margins_loading: action.payload
});
const updateMarginsCountReducer = (state: MiniNetworkState, action: PayloadAction<number>): MiniNetworkState => ({
    ...state, margins_count: action.payload
});

const addMiniNetworkContactReducer = (state: MiniNetworkState, action: PayloadAction<OrganisationContact>): MiniNetworkState => ({
    ...state, mini_network_contacts: [action.payload, ...state.mini_network_contacts]
});

const updateMiniNetworkContactReducer = (state: MiniNetworkState, action: PayloadAction<OrganisationContact>): MiniNetworkState => ({
    ...state, mini_network_contacts: state.mini_network_contacts.map((contact) => contact.id === action.payload.id ? action.payload : contact)
});

const updateMiniNetworkContactsReducer = (state: MiniNetworkState, action: PayloadAction<OrganisationContact[]>): MiniNetworkState => ({
    ...state, mini_network_contacts: action.payload
});

const deleteMiniNetworkContactReducer = (state: MiniNetworkState, action: PayloadAction<number>): MiniNetworkState => ({
    ...state, mini_network_contacts: state.mini_network_contacts.filter((contact) => contact.id !== action.payload)
});

const addMiniNetworkSubMiniNetworkReducer = (state: MiniNetworkState, action: PayloadAction<Network>): MiniNetworkState => ({
    ...state, mini_network_sub_networks: [action.payload, ...state.mini_network_sub_networks]
});

const updateMiniNetworkSubMiniNetworkReducer = (state: MiniNetworkState, action: PayloadAction<Network>): MiniNetworkState => ({
    ...state, mini_network_sub_networks: state.mini_network_sub_networks.map((sub_network) => sub_network.id === action.payload.id ? action.payload : sub_network)
});
const loadSubMiniNetworksReducer = (state: MiniNetworkState): MiniNetworkState => ({
    ...state, mini_network_sub_networks_loading: false
});
const updateMiniNetworkSubMiniNetworksReducer = (state: MiniNetworkState, action: PayloadAction<Network[]>): MiniNetworkState => ({
    ...state, mini_network_sub_networks: action.payload, mini_network_sub_networks_loading: false
});

const deleteMiniNetworkSubMiniNetworkReducer = (state: MiniNetworkState, action: PayloadAction<number>): MiniNetworkState => ({
    ...state, mini_network_sub_networks: state.mini_network_sub_networks.filter((sub_network) => sub_network.id !== action.payload)
});

const addMiniNetworkListConfigurationPreferenceReducer = (state: MiniNetworkState, action: PayloadAction<Preferences[]>): MiniNetworkState => ({
    ...state, mini_network_list_configuration: { ...state.mini_network_list_configuration, preferences: [...state.mini_network_list_configuration.preferences, ...action.payload] }
});

const setMiniNetworkListConfigurationPreferenceReducer = (state: MiniNetworkState, action: PayloadAction<Preferences[]>): MiniNetworkState => ({
    ...state, mini_network_list_configuration: { ...state.mini_network_list_configuration, preferences: action.payload }
});

const setPreferenceReducer = (state: MiniNetworkState, action: PayloadAction<Preferences[]>): MiniNetworkState => {
    let main_preference = {};
    if(action.payload.find(value => value.module === MINI_NETWORK_MODULE)) {
        const { preferences, columns, order_by, order } = setMiniNetworkPreference(action.payload, state.mini_network_list_configuration.columns);
        main_preference = {
            network_list_configuration: { ...state.mini_network_list_configuration, preferences, columns },
            network_list_criteria: { ...state.mini_network_list_criteria, order, order_by },
        };
    }
    let margin_pref = {};
    if(action.payload.find(value => value.module === MINI_NETWORK_MARGIN_MODULE)) {
        const { margins_preferences, margins_columns, margins_order_by, margins_order } = setMarginPreference(action.payload, state.margins_configuration.columns);
        margin_pref = {
            margins_configuration: { ...state.margins_configuration, preferences: margins_preferences, columns: margins_columns },
            margins_criteria: { ...state.margins_criteria, order: margins_order, order_by: margins_order_by }
        };
    }
    return {
        ...state,
        ...main_preference,
        ...margin_pref,
        first_preference_loaded: true
    };
};

const toggleColumnReducer = (state: MiniNetworkState, action: PayloadAction<number>): MiniNetworkState => {
    const columns = cloneDeep([...state.mini_network_list_configuration.columns]);
    const column = { ...columns[action.payload] };
    column.checked = !column.checked;
    columns[action.payload] = column;
    return { ...state, mini_network_list_configuration: { ...state.mini_network_list_configuration, columns } };
};

const updateMiniNetworkListReducer = (state: MiniNetworkState, action: PayloadAction<Network[]>): MiniNetworkState => ({
    ...state, mini_network_list: action.payload
});
const updateMiniNetworkListCriteriaReducer = (state: MiniNetworkState, action: PayloadAction<NetworkListCriteria>): MiniNetworkState => ({
    ...state, mini_network_list_criteria: action.payload
});
const updateMiniNetworkReducer = (state: MiniNetworkState, action: PayloadAction<Network>): MiniNetworkState => ({
    ...state, mini_network: action.payload
});
const updateMiniNetworkListLoadingReducer = (state: MiniNetworkState, action: PayloadAction<boolean>): MiniNetworkState => ({
    ...state, mini_network_list_loading: action.payload
});
const updateMiniNetworkListCountReducer = (state: MiniNetworkState, action: PayloadAction<number>): MiniNetworkState => ({
    ...state, mini_network_list_count: action.payload
});


const setColumnsReducer = (state: MiniNetworkState, action: PayloadAction<Column[]>): MiniNetworkState => ({
    ...state, mini_network_list_configuration: { ...state.mini_network_list_configuration, columns: action.payload },
});

export const miniNetwork = createSlice({
    name: 'mini_network',
    initialState,
    reducers: {
        setSuspendedMargin: setSuspendedMarginReducer,
        addMarginsConfigurationPreference: addMarginsConfigurationPreferenceReducer,
        setMarginsConfigurationPreference: setMarginsConfigurationPreferenceReducer,
        moveDownColumnMargins: moveDownColumnMarginsReducer,
        moveUpColumnMargins: moveUpColumnMarginsReducer,
        toggleColumnMargins: toggleColumnMarginsReducer,
        setMiniNetworkMargins: setNetworkMarginsReducer,
        updateMarginsCriteria: updateMarginsCriteriaReducer,
        updateMargin: updateMarginReducer,
        updateMarginsLoading: updateMarginsLoadingReducer,
        updateMarginsCount: updateMarginsCountReducer,
        addMiniNetworkContact: addMiniNetworkContactReducer,
        updateMiniNetworkContact: updateMiniNetworkContactReducer,
        updateMiniNetworkContacts: updateMiniNetworkContactsReducer,
        deleteMiniNetworkContact: deleteMiniNetworkContactReducer,
        addMiniNetworkSubMiniNetwork: addMiniNetworkSubMiniNetworkReducer,
        updateMiniNetworkSubMiniNetwork: updateMiniNetworkSubMiniNetworkReducer,
        loadSubMiniNetworks: loadSubMiniNetworksReducer,
        updateMiniNetworkSubMiniNetworks: updateMiniNetworkSubMiniNetworksReducer,
        deleteMiniNetworkSubMiniNetwork: deleteMiniNetworkSubMiniNetworkReducer,
        addMiniNetworkListConfigurationPreference: addMiniNetworkListConfigurationPreferenceReducer,
        setMiniNetworkListConfigurationPreference: setMiniNetworkListConfigurationPreferenceReducer,
        toggleColumn: toggleColumnReducer,
        updateMiniNetworkList: updateMiniNetworkListReducer,
        updateMiniNetworkListCriteria: updateMiniNetworkListCriteriaReducer,
        updateMiniNetwork: updateMiniNetworkReducer,
        updateMiniNetworkListLoading: updateMiniNetworkListLoadingReducer,
        updateMiniNetworkListCount: updateMiniNetworkListCountReducer,
        setColumns: setColumnsReducer
    },
    extraReducers: (builder) => {
        builder
            .addCase(setPreferences, setPreferenceReducer);
    }
});

export const {
    setSuspendedMargin,
    addMarginsConfigurationPreference,
    setMarginsConfigurationPreference,
    moveDownColumnMargins,
    moveUpColumnMargins,
    toggleColumnMargins,
    setMiniNetworkMargins,
    updateMarginsCriteria,
    updateMargin,
    updateMarginsLoading,
    updateMarginsCount,
    addMiniNetworkContact,
    updateMiniNetworkContact,
    updateMiniNetworkContacts,
    deleteMiniNetworkContact,
    addMiniNetworkSubMiniNetwork,
    updateMiniNetworkSubMiniNetwork,
    loadSubMiniNetworks,
    updateMiniNetworkSubMiniNetworks,
    deleteMiniNetworkSubMiniNetwork,
    addMiniNetworkListConfigurationPreference,
    setMiniNetworkListConfigurationPreference,
    toggleColumn,
    updateMiniNetworkList,
    updateMiniNetworkListCriteria,
    updateMiniNetwork,
    updateMiniNetworkListLoading,
    updateMiniNetworkListCount,
    setColumns
} = miniNetwork.actions;